'use client';

import React, { useState } from 'react';
import { Save, Settings, AlertTriangle, CheckCircle, Shield, Hash, ArrowRightLeft, GitBranch, Home, Info,  } from 'lucide-react';
import Link from 'next/link';
import { useRole } from '@/lib/useRole';

// ─── Types ────────────────────────────────────────────────────────────────────

interface MovementTypeConfig {
  id: string;
  key: string;
  label: string;
  direction: 'in' | 'out' | 'neutral';
  enabled: boolean;
  requiresApproval: boolean;
  requiresReason: boolean;
}

interface AlertThresholds {
  dailyMortalityPct: number;
  weeklyMortalityPct: number;
  monthlyMortalityPct: number;
  cumulativeMortalityPct: number;
  populationVariancePct: number;
  houseCapacityWarningPct: number;
  houseCapacityCriticalPct: number;
  largeAdjustmentQty: number;
  transferVariancePct: number;
  missingDailyRecordDays: number;
}

interface ApprovalWorkflow {
  movementApprovalRequired: boolean;
  transferApprovalRequired: boolean;
  cullingApprovalRequired: boolean;
  saleApprovalRequired: boolean;
  adjustmentApprovalRequired: boolean;
  reconciliationApprovalRequired: boolean;
  largeMovementThreshold: number;
  largeMovementApprovalRequired: boolean;
  reversalApprovalRequired: boolean;
  autoApproveBelow: number;
}

interface HouseCapacitySettings {
  enforceCapacityLimits: boolean;
  allowOverCapacityWithApproval: boolean;
  overCapacityApprovalThreshold: number;
  showOccupancyWarnings: boolean;
  defaultCapacityPerSqm: number;
}

interface NumberingFormat {
  prefix: string;
  includeYear: boolean;
  yearFormat: '2-digit' | '4-digit';
  separator: string;
  sequenceLength: number;
  resetAnnually: boolean;
  preview: string;
}

interface BirdSettings {
  movementTypes: MovementTypeConfig[];
  alertThresholds: AlertThresholds;
  approvalWorkflow: ApprovalWorkflow;
  houseCapacity: HouseCapacitySettings;
  numberingFormat: NumberingFormat;
}

// ─── Default Settings ─────────────────────────────────────────────────────────

const DEFAULT_MOVEMENT_TYPES: MovementTypeConfig[] = [
  { id: 'mt-1', key: 'initial_placement', label: 'Initial Placement', direction: 'in', enabled: true, requiresApproval: false, requiresReason: false },
  { id: 'mt-2', key: 'purchase', label: 'Purchase / Acquisition', direction: 'in', enabled: true, requiresApproval: true, requiresReason: true },
  { id: 'mt-3', key: 'hatch', label: 'Hatch', direction: 'in', enabled: true, requiresApproval: false, requiresReason: false },
  { id: 'mt-4', key: 'transfer_in', label: 'Transfer In', direction: 'in', enabled: true, requiresApproval: true, requiresReason: true },
  { id: 'mt-5', key: 'transfer_out', label: 'Transfer Out', direction: 'out', enabled: true, requiresApproval: true, requiresReason: true },
  { id: 'mt-6', key: 'mortality', label: 'Mortality', direction: 'out', enabled: true, requiresApproval: false, requiresReason: true },
  { id: 'mt-7', key: 'culling', label: 'Culling', direction: 'out', enabled: true, requiresApproval: true, requiresReason: true },
  { id: 'mt-8', key: 'sale', label: 'Bird Sale', direction: 'out', enabled: true, requiresApproval: true, requiresReason: false },
  { id: 'mt-9', key: 'return', label: 'Return', direction: 'in', enabled: true, requiresApproval: true, requiresReason: true },
  { id: 'mt-10', key: 'positive_adjustment', label: 'Positive Adjustment', direction: 'in', enabled: true, requiresApproval: true, requiresReason: true },
  { id: 'mt-11', key: 'negative_adjustment', label: 'Negative Adjustment', direction: 'out', enabled: true, requiresApproval: true, requiresReason: true },
  { id: 'mt-12', key: 'reversal', label: 'Reversal', direction: 'neutral', enabled: true, requiresApproval: true, requiresReason: true },
  { id: 'mt-13', key: 'other', label: 'Other Authorized Movement', direction: 'neutral', enabled: true, requiresApproval: true, requiresReason: true },
];

const DEFAULT_SETTINGS: BirdSettings = {
  movementTypes: DEFAULT_MOVEMENT_TYPES,
  alertThresholds: {
    dailyMortalityPct: 0.5,
    weeklyMortalityPct: 2.0,
    monthlyMortalityPct: 5.0,
    cumulativeMortalityPct: 10.0,
    populationVariancePct: 2.0,
    houseCapacityWarningPct: 85,
    houseCapacityCriticalPct: 95,
    largeAdjustmentQty: 50,
    transferVariancePct: 2.0,
    missingDailyRecordDays: 1,
  },
  approvalWorkflow: {
    movementApprovalRequired: true,
    transferApprovalRequired: true,
    cullingApprovalRequired: true,
    saleApprovalRequired: true,
    adjustmentApprovalRequired: true,
    reconciliationApprovalRequired: true,
    largeMovementThreshold: 500,
    largeMovementApprovalRequired: true,
    reversalApprovalRequired: true,
    autoApproveBelow: 10,
  },
  houseCapacity: {
    enforceCapacityLimits: true,
    allowOverCapacityWithApproval: true,
    overCapacityApprovalThreshold: 5,
    showOccupancyWarnings: true,
    defaultCapacityPerSqm: 10,
  },
  numberingFormat: {
    prefix: 'BRM',
    includeYear: true,
    yearFormat: '4-digit',
    separator: '-',
    sequenceLength: 6,
    resetAnnually: true,
    preview: 'BRM-2026-000001',
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildPreview(fmt: NumberingFormat): string {
  const year = fmt.includeYear
    ? fmt.yearFormat === '4-digit' ?'2026' :'26'
    : null;
  const seq = '1'.padStart(fmt.sequenceLength, '0');
  const parts = [fmt.prefix, ...(year ? [year] : []), seq];
  return parts.join(fmt.separator || '-');
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionCard({ title, icon, description, children }: {
  title: string;
  icon: React.ReactNode;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <div className="mb-4">
        <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
          {icon}
          {title}
        </h2>
        {description && <p className="text-xs text-gray-400 mt-1">{description}</p>}
      </div>
      {children}
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${checked ? 'bg-green-600' : 'bg-gray-200'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

const inputCls = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white';
const labelCls = 'block text-xs font-semibold text-gray-600 mb-1';

// ─── Main Component ───────────────────────────────────────────────────────────

export default function BirdSettingsContent() {
  const [settings, setSettings] = useState<BirdSettings>(DEFAULT_SETTINGS);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState<'movement-types' | 'alerts' | 'approvals' | 'capacity' | 'numbering'>('movement-types');
  const { can } = useRole();

  if (!can('bird_records.view')) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center">
          <Shield size={28} className="text-red-400" />
        </div>
        <h2 className="text-lg font-bold text-gray-700">Access Denied</h2>
        <p className="text-sm text-gray-400">You do not have permission to manage bird records settings.</p>
        <Link href="/bird-records" className="px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 transition-colors">
          Back to Bird Records
        </Link>
      </div>
    );
  }

  function handleSave() {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  function updateThreshold<K extends keyof AlertThresholds>(key: K, value: AlertThresholds[K]) {
    setSettings(prev => ({ ...prev, alertThresholds: { ...prev.alertThresholds, [key]: value } }));
    setSaved(false);
  }

  function updateApproval<K extends keyof ApprovalWorkflow>(key: K, value: ApprovalWorkflow[K]) {
    setSettings(prev => ({ ...prev, approvalWorkflow: { ...prev.approvalWorkflow, [key]: value } }));
    setSaved(false);
  }

  function updateCapacity<K extends keyof HouseCapacitySettings>(key: K, value: HouseCapacitySettings[K]) {
    setSettings(prev => ({ ...prev, houseCapacity: { ...prev.houseCapacity, [key]: value } }));
    setSaved(false);
  }

  function updateNumbering<K extends keyof NumberingFormat>(key: K, value: NumberingFormat[K]) {
    setSettings(prev => {
      const updated = { ...prev.numberingFormat, [key]: value };
      updated.preview = buildPreview(updated);
      return { ...prev, numberingFormat: updated };
    });
    setSaved(false);
  }

  function toggleMovementType(id: string, field: 'enabled' | 'requiresApproval' | 'requiresReason') {
    setSettings(prev => ({
      ...prev,
      movementTypes: prev.movementTypes.map(mt =>
        mt.id === id ? { ...mt, [field]: !mt[field] } : mt
      ),
    }));
    setSaved(false);
  }

  const tabs = [
    { id: 'movement-types', label: 'Movement Types', icon: <ArrowRightLeft size={14} /> },
    { id: 'alerts', label: 'Alert Thresholds', icon: <AlertTriangle size={14} /> },
    { id: 'approvals', label: 'Approval Workflows', icon: <GitBranch size={14} /> },
    { id: 'capacity', label: 'House Capacity', icon: <Home size={14} /> },
    { id: 'numbering', label: 'Auto-Numbering', icon: <Hash size={14} /> },
  ] as const;

  const directionBadge = (dir: 'in' | 'out' | 'neutral') => {
    if (dir === 'in') return <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">IN</span>;
    if (dir === 'out') return <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">OUT</span>;
    return <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">NEUTRAL</span>;
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Bird Records Settings</h1>
          <p className="text-sm text-gray-500 mt-0.5">Configure movement types, alert thresholds, approval workflows, house capacity limits, and auto-numbering for all bird operations.</p>
        </div>
        <button
          onClick={handleSave}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 transition-colors shadow-sm"
        >
          <Save size={15} />
          Save Settings
        </button>
      </div>

      {/* Save confirmation */}
      {saved && (
        <div className="mb-4 flex items-center gap-3 bg-green-50 border border-green-200 text-green-800 text-sm px-4 py-3 rounded-xl">
          <CheckCircle size={16} />
          Settings saved successfully.
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 overflow-x-auto pb-1 scrollbar-thin">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'bg-green-600 text-white shadow-sm'
                : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Movement Types ── */}
      {activeTab === 'movement-types' && (
        <SectionCard
          title="Movement Types"
          icon={<ArrowRightLeft size={16} className="text-green-600" />}
          description="Enable or disable movement types and configure their approval and reason requirements."
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-semibold text-gray-500 pb-2 pr-4">Movement Type</th>
                  <th className="text-center text-xs font-semibold text-gray-500 pb-2 px-3">Direction</th>
                  <th className="text-center text-xs font-semibold text-gray-500 pb-2 px-3">Enabled</th>
                  <th className="text-center text-xs font-semibold text-gray-500 pb-2 px-3">Requires Approval</th>
                  <th className="text-center text-xs font-semibold text-gray-500 pb-2 px-3">Requires Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {settings.movementTypes.map(mt => (
                  <tr key={mt.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-3 pr-4">
                      <span className="font-medium text-gray-800">{mt.label}</span>
                    </td>
                    <td className="py-3 px-3 text-center">
                      {directionBadge(mt.direction)}
                    </td>
                    <td className="py-3 px-3 text-center">
                      <div className="flex justify-center">
                        <Toggle checked={mt.enabled} onChange={() => toggleMovementType(mt.id, 'enabled')} />
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <div className="flex justify-center">
                        <Toggle
                          checked={mt.requiresApproval}
                          onChange={() => toggleMovementType(mt.id, 'requiresApproval')}
                        />
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <div className="flex justify-center">
                        <Toggle
                          checked={mt.requiresReason}
                          onChange={() => toggleMovementType(mt.id, 'requiresReason')}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg">
            <Info size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-700">Disabled movement types will not appear in the movement recording form. Changes take effect immediately for new records.</p>
          </div>
        </SectionCard>
      )}

      {/* ── Alert Thresholds ── */}
      {activeTab === 'alerts' && (
        <div className="space-y-5">
          <SectionCard
            title="Mortality Alert Thresholds"
            icon={<AlertTriangle size={16} className="text-red-500" />}
            description="Configure when mortality alerts are triggered. Alerts fire when the rate exceeds these thresholds."
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { key: 'dailyMortalityPct' as const, label: 'Daily Mortality (%)', desc: 'Alert when daily mortality exceeds this % of flock.' },
                { key: 'weeklyMortalityPct' as const, label: 'Weekly Mortality (%)', desc: 'Alert when 7-day mortality exceeds this %.' },
                { key: 'monthlyMortalityPct' as const, label: 'Monthly Mortality (%)', desc: 'Alert when 30-day mortality exceeds this %.' },
                { key: 'cumulativeMortalityPct' as const, label: 'Cumulative Mortality (%)', desc: 'Alert when total flock mortality exceeds this %.' },
              ].map(({ key, label, desc }) => (
                <div key={key}>
                  <label className={labelCls}>{label}</label>
                  <input
                    type="number"
                    value={settings.alertThresholds[key]}
                    onChange={e => updateThreshold(key, Number(e.target.value))}
                    className={inputCls}
                    min="0"
                    max="100"
                    step="0.1"
                  />
                  <p className="text-xs text-gray-400 mt-1">{desc}</p>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard
            title="Population & Variance Alerts"
            icon={<AlertTriangle size={16} className="text-amber-500" />}
            description="Thresholds for population discrepancies, capacity warnings, and unusual movements."
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { key: 'populationVariancePct' as const, label: 'Population Variance (%)', desc: 'Alert when reconciliation variance exceeds this %.' },
                { key: 'houseCapacityWarningPct' as const, label: 'House Capacity Warning (%)', desc: 'Show warning when occupancy reaches this %.' },
                { key: 'houseCapacityCriticalPct' as const, label: 'House Capacity Critical (%)', desc: 'Show critical alert when occupancy reaches this %.' },
                { key: 'largeAdjustmentQty' as const, label: 'Large Adjustment Threshold (birds)', desc: 'Flag adjustments above this quantity for review.' },
                { key: 'transferVariancePct' as const, label: 'Transfer Variance (%)', desc: 'Alert when dispatched vs received variance exceeds this %.' },
                { key: 'missingDailyRecordDays' as const, label: 'Missing Record Alert (days)', desc: 'Alert when daily records are missing for this many days.' },
              ].map(({ key, label, desc }) => (
                <div key={key}>
                  <label className={labelCls}>{label}</label>
                  <input
                    type="number"
                    value={settings.alertThresholds[key]}
                    onChange={e => updateThreshold(key, Number(e.target.value))}
                    className={inputCls}
                    min="0"
                    step={key === 'largeAdjustmentQty' || key === 'missingDailyRecordDays' ? '1' : '0.1'}
                  />
                  <p className="text-xs text-gray-400 mt-1">{desc}</p>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Alert severity reference */}
          <SectionCard
            title="Alert Severity Reference"
            icon={<Info size={16} className="text-blue-500" />}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { label: 'Critical', desc: 'Immediate action required — population at risk', color: 'bg-red-50 border-red-200', badge: 'bg-red-100 text-red-700' },
                { label: 'Warning', desc: 'Attention needed — approaching threshold', color: 'bg-amber-50 border-amber-200', badge: 'bg-amber-100 text-amber-700' },
                { label: 'Info', desc: 'Informational — review recommended', color: 'bg-blue-50 border-blue-200', badge: 'bg-blue-100 text-blue-700' },
                { label: 'Normal', desc: 'Within acceptable range', color: 'bg-green-50 border-green-200', badge: 'bg-green-100 text-green-700' },
              ].map(s => (
                <div key={s.label} className={`flex items-center gap-3 p-3 rounded-xl border ${s.color}`}>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${s.badge}`}>{s.label}</span>
                  <span className="text-xs text-gray-600">{s.desc}</span>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      )}

      {/* ── Approval Workflows ── */}
      {activeTab === 'approvals' && (
        <div className="space-y-5">
          <SectionCard
            title="Module-Level Approval Requirements"
            icon={<GitBranch size={16} className="text-purple-600" />}
            description="Configure which operations require manager approval before affecting bird population."
          >
            <div className="space-y-3">
              {[
                { key: 'movementApprovalRequired' as const, label: 'Bird Movements', desc: 'Require approval for general bird movement records before they are posted.' },
                { key: 'transferApprovalRequired' as const, label: 'Bird Transfers', desc: 'Require approval before a transfer is dispatched and population is updated.' },
                { key: 'cullingApprovalRequired' as const, label: 'Culling Records', desc: 'Require authorization before culling records reduce flock population.' },
                { key: 'saleApprovalRequired' as const, label: 'Bird Sales', desc: 'Require confirmation before a sale is posted as a population-OUT movement.' },
                { key: 'adjustmentApprovalRequired' as const, label: 'Population Adjustments', desc: 'Require approval for all positive and negative population adjustments.' },
                { key: 'reconciliationApprovalRequired' as const, label: 'Reconciliation Adjustments', desc: 'Require approval before reconciliation variances are corrected.' },
                { key: 'reversalApprovalRequired' as const, label: 'Transaction Reversals', desc: 'Require approval before any posted transaction can be reversed.' },
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-start justify-between gap-4 py-3 border-b border-gray-50 last:border-0">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{label}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{desc}</p>
                  </div>
                  <Toggle
                    checked={settings.approvalWorkflow[key]}
                    onChange={v => updateApproval(key, v)}
                  />
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard
            title="Large Movement Controls"
            icon={<AlertTriangle size={16} className="text-amber-500" />}
            description="Additional approval requirements for movements above a defined quantity threshold."
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Large Movement Threshold (birds)</label>
                <input
                  type="number"
                  value={settings.approvalWorkflow.largeMovementThreshold}
                  onChange={e => updateApproval('largeMovementThreshold', Number(e.target.value))}
                  className={inputCls}
                  min="1"
                />
                <p className="text-xs text-gray-400 mt-1">Movements above this quantity are flagged as large movements.</p>
              </div>
              <div>
                <label className={labelCls}>Auto-Approve Below (birds)</label>
                <input
                  type="number"
                  value={settings.approvalWorkflow.autoApproveBelow}
                  onChange={e => updateApproval('autoApproveBelow', Number(e.target.value))}
                  className={inputCls}
                  min="0"
                />
                <p className="text-xs text-gray-400 mt-1">Movements below this quantity are auto-approved (set 0 to disable).</p>
              </div>
            </div>
            <div className="mt-4 flex items-start justify-between gap-4 py-3 border-t border-gray-100">
              <div>
                <p className="text-sm font-semibold text-gray-800">Require Approval for Large Movements</p>
                <p className="text-xs text-gray-400 mt-0.5">Always require senior approval when movement quantity exceeds the threshold above.</p>
              </div>
              <Toggle
                checked={settings.approvalWorkflow.largeMovementApprovalRequired}
                onChange={v => updateApproval('largeMovementApprovalRequired', v)}
              />
            </div>
          </SectionCard>

          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-xl">
            <Info size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700">
              <strong>Important:</strong> Draft and rejected movements never affect bird population. Only approved/posted records update the authoritative population ledger.
            </p>
          </div>
        </div>
      )}

      {/* ── House Capacity ── */}
      {activeTab === 'capacity' && (
        <div className="space-y-5">
          <SectionCard
            title="House Capacity Enforcement"
            icon={<Home size={16} className="text-blue-600" />}
            description="Control how the system handles bird placements and transfers that approach or exceed house capacity."
          >
            <div className="space-y-3">
              {[
                { key: 'enforceCapacityLimits' as const, label: 'Enforce Capacity Limits', desc: 'Prevent placements and transfers that would exceed the configured house capacity.' },
                { key: 'allowOverCapacityWithApproval' as const, label: 'Allow Over-Capacity with Approval', desc: 'Permit over-capacity placements when explicitly approved by a manager.' },
                { key: 'showOccupancyWarnings' as const, label: 'Show Occupancy Warnings', desc: 'Display capacity warnings on house cards and movement forms when approaching limits.' },
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-start justify-between gap-4 py-3 border-b border-gray-50 last:border-0">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{label}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{desc}</p>
                  </div>
                  <Toggle
                    checked={settings.houseCapacity[key] as boolean}
                    onChange={v => updateCapacity(key, v)}
                  />
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard
            title="Capacity Thresholds & Defaults"
            icon={<Settings size={16} className="text-gray-600" />}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Over-Capacity Approval Threshold (%)</label>
                <input
                  type="number"
                  value={settings.houseCapacity.overCapacityApprovalThreshold}
                  onChange={e => updateCapacity('overCapacityApprovalThreshold', Number(e.target.value))}
                  className={inputCls}
                  min="0"
                  max="50"
                  step="1"
                />
                <p className="text-xs text-gray-400 mt-1">Maximum % over capacity that can be approved (e.g. 5 = up to 105% of capacity).</p>
              </div>
              <div>
                <label className={labelCls}>Default Capacity per m² (birds)</label>
                <input
                  type="number"
                  value={settings.houseCapacity.defaultCapacityPerSqm}
                  onChange={e => updateCapacity('defaultCapacityPerSqm', Number(e.target.value))}
                  className={inputCls}
                  min="1"
                  step="1"
                />
                <p className="text-xs text-gray-400 mt-1">Used when a house does not have an explicit capacity set.</p>
              </div>
            </div>
          </SectionCard>

          {/* Occupancy level reference */}
          <SectionCard
            title="Occupancy Level Reference"
            icon={<Info size={16} className="text-blue-500" />}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: 'Optimal', range: `< ${settings.alertThresholds.houseCapacityWarningPct}%`, color: 'bg-green-50 border-green-200', badge: 'bg-green-100 text-green-700' },
                { label: 'Warning', range: `${settings.alertThresholds.houseCapacityWarningPct}% – ${settings.alertThresholds.houseCapacityCriticalPct}%`, color: 'bg-amber-50 border-amber-200', badge: 'bg-amber-100 text-amber-700' },
                { label: 'Critical', range: `≥ ${settings.alertThresholds.houseCapacityCriticalPct}%`, color: 'bg-red-50 border-red-200', badge: 'bg-red-100 text-red-700' },
                { label: 'Over Capacity', range: '> 100%', color: 'bg-red-100 border-red-300', badge: 'bg-red-200 text-red-800' },
              ].map(s => (
                <div key={s.label} className={`flex flex-col gap-1 p-3 rounded-xl border ${s.color}`}>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold self-start ${s.badge}`}>{s.label}</span>
                  <span className="text-xs text-gray-600">{s.range}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-3">Warning and critical thresholds are configured in the Alert Thresholds tab.</p>
          </SectionCard>
        </div>
      )}

      {/* ── Auto-Numbering ── */}
      {activeTab === 'numbering' && (
        <div className="space-y-5">
          <SectionCard
            title="Movement Number Format"
            icon={<Hash size={16} className="text-green-600" />}
            description="Configure the auto-generated movement number format. Numbers are generated sequentially and cannot be edited manually."
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>Prefix</label>
                <input
                  type="text"
                  value={settings.numberingFormat.prefix}
                  onChange={e => updateNumbering('prefix', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                  className={inputCls}
                  maxLength={6}
                  placeholder="BRM"
                />
                <p className="text-xs text-gray-400 mt-1">Letters and numbers only. Max 6 characters.</p>
              </div>

              <div>
                <label className={labelCls}>Separator</label>
                <select
                  value={settings.numberingFormat.separator}
                  onChange={e => updateNumbering('separator', e.target.value)}
                  className={inputCls}
                >
                  <option value="-">Hyphen ( - )</option>
                  <option value="/">Slash ( / )</option>
                  <option value=".">Period ( . )</option>
                  <option value="">None</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">Character between prefix, year, and sequence.</p>
              </div>

              <div>
                <label className={labelCls}>Sequence Length (digits)</label>
                <select
                  value={settings.numberingFormat.sequenceLength}
                  onChange={e => updateNumbering('sequenceLength', Number(e.target.value))}
                  className={inputCls}
                >
                  {[4, 5, 6, 7, 8].map(n => (
                    <option key={n} value={n}>{n} digits (e.g. {'0'.repeat(n - 1)}1)</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">Zero-padded sequence number length.</p>
              </div>

              <div>
                <label className={labelCls}>Include Year</label>
                <div className="flex items-center gap-3 mt-2">
                  <Toggle
                    checked={settings.numberingFormat.includeYear}
                    onChange={v => updateNumbering('includeYear', v)}
                  />
                  <span className="text-xs text-gray-500">{settings.numberingFormat.includeYear ? 'Year included in number' : 'No year in number'}</span>
                </div>
              </div>

              {settings.numberingFormat.includeYear && (
                <div>
                  <label className={labelCls}>Year Format</label>
                  <select
                    value={settings.numberingFormat.yearFormat}
                    onChange={e => updateNumbering('yearFormat', e.target.value as '2-digit' | '4-digit')}
                    className={inputCls}
                  >
                    <option value="4-digit">4-digit (2026)</option>
                    <option value="2-digit">2-digit (26)</option>
                  </select>
                </div>
              )}

              <div>
                <label className={labelCls}>Reset Sequence Annually</label>
                <div className="flex items-center gap-3 mt-2">
                  <Toggle
                    checked={settings.numberingFormat.resetAnnually}
                    onChange={v => updateNumbering('resetAnnually', v)}
                  />
                  <span className="text-xs text-gray-500">{settings.numberingFormat.resetAnnually ? 'Resets to 000001 each year' : 'Continuous sequence'}</span>
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="mt-5 p-4 bg-gray-50 rounded-xl border border-gray-200">
              <p className="text-xs font-semibold text-gray-500 mb-2">PREVIEW</p>
              <div className="flex items-center gap-3 flex-wrap">
                {[1, 2, 3].map(n => (
                  <span key={n} className="font-mono text-sm font-bold text-green-700 bg-green-50 border border-green-200 px-3 py-1.5 rounded-lg">
                    {buildPreview({ ...settings.numberingFormat }).replace(/\d+$/, String(n).padStart(settings.numberingFormat.sequenceLength, '0'))}
                  </span>
                ))}
                <span className="text-xs text-gray-400">...</span>
              </div>
              <p className="text-xs text-gray-400 mt-2">Numbers are generated automatically. Manual editing is not permitted.</p>
            </div>
          </SectionCard>

          <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded-xl">
            <Info size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-700">
              <strong>Note:</strong> Changing the numbering format does not affect existing movement numbers. New numbers will use the updated format from the next movement onwards.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
